import React from 'react';
import { Battery, Plug, Sun, Unplug, X, Zap } from 'lucide-react';
import type { ElectricalGraphPinSide, PortRoleKind, PowerInputKind, PowerOutputKind } from '../../../types/deviceModel';

export const CARD_W = 220;
export const CARD_H = 76;
export const CARD_H_POLES = 90;
export const CARD_HEADER_H = 40;
export const PIN_SIDES: ElectricalGraphPinSide[] = ['left', 'right', 'top', 'bottom'];

export type ElectricalCardTone =
  | 'poe-in'
  | 'poe-in-role'
  | 'poe-out'
  | 'ac-in'
  | 'ac-out'
  | 'dc-in'
  | 'dc-out'
  | 'pv'
  | 'battery';

export const CARD_TONES: Record<ElectricalCardTone, { header: string; headerHi: string; pip: string; wire: string }> = {
  'poe-in': { header: '#6d28d9', headerHi: '#8b5cf6', pip: '#c4b5fd', wire: '#a78bfa' },
  'poe-in-role': { header: '#0e7490', headerHi: '#06b6d4', pip: '#67e8f9', wire: '#22d3ee' },
  'poe-out': { header: '#c2410c', headerHi: '#ea580c', pip: '#fdba74', wire: '#fb923c' },
  'ac-in': { header: '#b91c1c', headerHi: '#dc2626', pip: '#fca5a5', wire: '#f87171' },
  'ac-out': { header: '#9f1239', headerHi: '#e11d48', pip: '#fda4af', wire: '#fb7185' },
  'dc-in': { header: '#a16207', headerHi: '#ca8a04', pip: '#fde047', wire: '#eab308' },
  'dc-out': { header: '#854d0e', headerHi: '#b45309', pip: '#fcd34d', wire: '#f59e0b' },
  pv: { header: '#15803d', headerHi: '#22c55e', pip: '#86efac', wire: '#4ade80' },
  battery: { header: '#0f766e', headerHi: '#14b8a6', pip: '#5eead4', wire: '#2dd4bf' },
};

export function toneForPowerInput(kind: PowerInputKind): ElectricalCardTone {
  if (kind === 'poe-in') return 'poe-in';
  if (kind === 'pv') return 'pv';
  if (kind === 'battery') return 'battery';
  if (kind === 'ac') return 'ac-in';
  return 'dc-in';
}

export function toneForPowerOutput(kind: PowerOutputKind): ElectricalCardTone {
  return kind === 'ac' ? 'ac-out' : 'dc-out';
}

export function toneForPortRole(kind: PortRoleKind): ElectricalCardTone {
  return kind === 'poe-in' ? 'poe-in-role' : 'poe-out';
}

interface ElectricalCardNodeProps {
  id: string;
  title: string;
  subtitle: string;
  summary: string;
  tone: ElectricalCardTone;
  live: boolean;
  selected: boolean;
  x: number;
  y: number;
  poles?: 'single' | 'plusMinus';
  pinId?: string;
  plusPinId?: string;
  minusPinId?: string;
  onPointerDown: (e: React.PointerEvent) => void;
  onPinDown: (pin: 'out' | 'plus' | 'minus', e: React.PointerEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  pinsOpen?: boolean;
}

const pipClass = 'absolute w-3 h-3 rounded-full border border-white/80 z-10 opacity-0 pointer-events-none cursor-grab transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto data-[live]:opacity-100 data-[live]:pointer-events-auto group-data-[pins-open]:opacity-100 group-data-[pins-open]:pointer-events-auto';

const ToneIcon: React.FC<{ tone: ElectricalCardTone }> = ({ tone }) => {
  const cls = 'text-white/90 shrink-0';
  if (tone === 'poe-in' || tone === 'poe-in-role' || tone === 'poe-out') return <Zap size={14} className={cls} />;
  if (tone === 'pv') return <Sun size={14} className={cls} />;
  if (tone === 'battery') return <Battery size={14} className={cls} />;
  if (tone === 'ac-out' || tone === 'dc-out') return <Unplug size={14} className={cls} />;
  return <Plug size={14} className={cls} />;
};

export function BlueprintPortPips({ pinId }: { pinId: string }) {
  return (
    <>
      {PIN_SIDES.map((side) => (
        <span
          key={side}
          data-graph-pin={pinId}
          data-pin-side={side}
          className={`blueprint-port-pip blueprint-port-pip-${side}`}
        />
      ))}
    </>
  );
}

const ElectricalCardNode: React.FC<ElectricalCardNodeProps> = ({
  id,
  title,
  subtitle,
  summary,
  tone,
  live,
  selected,
  x,
  y,
  poles = 'single',
  pinId,
  plusPinId,
  minusPinId,
  onPointerDown,
  onPinDown,
  onDelete,
  pinsOpen = false,
}) => {
  const colors = CARD_TONES[tone];
  const h = poles === 'plusMinus' ? CARD_H_POLES : CARD_H;
  const bodyTop = CARD_HEADER_H;

  const singlePip = (side: ElectricalGraphPinSide) => {
    const sideClass: Record<ElectricalGraphPinSide, string> = {
      right: 'right-0 translate-x-1/2 -translate-y-1/2',
      left: 'left-0 -translate-x-1/2 -translate-y-1/2',
      top: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2',
      bottom: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2',
    };
    const style: React.CSSProperties = { backgroundColor: colors.pip };
    if (side === 'left' || side === 'right') style.top = bodyTop + (h - bodyTop) / 2;
    return (
      <button
        key={side}
        type="button"
        data-graph-pin={pinId}
        data-pin-side={side}
        className={`${pipClass} ${sideClass[side]}`}
        style={style}
        title="Drag to a port, or drag a wire end onto this pip"
        onPointerDown={(e) => {
          e.stopPropagation();
          onPinDown('out', e);
        }}
      />
    );
  };

  const polePip = (pole: 'plus' | 'minus', side: ElectricalGraphPinSide, className: string) => (
    <button
      key={`${pole}-${side}`}
      type="button"
      data-graph-pin={pole === 'plus' ? plusPinId : minusPinId}
      data-pin-side={side}
      className={`${pipClass} ${className} ${pole === 'plus' ? 'bg-red-500' : 'bg-zinc-900'}`}
      title={pole === 'plus' ? '+' : '−'}
      onPointerDown={(e) => {
        e.stopPropagation();
        onPinDown(pole, e);
      }}
    />
  );

  return (
    <div
      data-blueprint-card="true"
      data-card-id={id}
      data-pins-open={pinsOpen ? 'true' : undefined}
      className="group absolute rounded-md shadow-lg select-none pointer-events-auto overflow-visible"
      style={{
        left: x,
        top: y,
        width: CARD_W,
        height: h,
        cursor: 'grab',
        zIndex: 20,
        boxShadow: selected
          ? `0 0 0 1.5px #f8d36a, 0 8px 18px rgba(0,0,0,0.45)`
          : `0 0 0 1px rgba(0,0,0,0.65), 0 8px 16px rgba(0,0,0,0.35)`,
      }}
      onPointerDown={onPointerDown}
    >
      <div
        className="rounded-md overflow-hidden h-full flex flex-col"
        style={{ background: 'rgba(16,16,18,0.94)' }}
      >
        <div
          className="relative flex items-center gap-1.5 px-2.5 pr-6 shrink-0"
          style={{
            height: CARD_HEADER_H,
            background: `linear-gradient(180deg, ${colors.headerHi} 0%, ${colors.header} 100%)`,
            boxShadow: live ? 'inset 0 -2px 0 #4ade80' : undefined,
          }}
        >
          <ToneIcon tone={tone} />
          <div className="min-w-0 leading-tight">
            <div className="text-[13px] font-semibold text-white truncate">{title}</div>
            <div className="text-[11px] italic text-white/70 truncate">{subtitle}</div>
          </div>
        </div>
        <div className="flex-1 px-3 py-1.5 min-h-0 flex items-center">
          <div className="text-[12px] text-white/55 truncate w-full">{summary}</div>
          {poles === 'plusMinus' ? (
            <div className="ml-2 shrink-0 flex gap-2 text-[11px] font-semibold tracking-wide">
              <span className="text-red-400">+</span>
              <span className="text-zinc-400">−</span>
            </div>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        className="absolute top-1 right-1 w-4 h-4 rounded flex items-center justify-center text-white/70 hover:text-white hover:bg-black/25 z-20"
        title="Delete card"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(e);
        }}
      >
        <X size={11} />
      </button>
      {poles === 'single' ? (
        <>
          {singlePip('left')}
          {singlePip('right')}
          {singlePip('top')}
          {singlePip('bottom')}
        </>
      ) : (
        <>
          {polePip('plus', 'right', 'right-0 top-[48px] translate-x-1/2')}
          {polePip('minus', 'right', 'right-0 bottom-2 translate-x-1/2')}
          {polePip('plus', 'left', 'left-0 top-[48px] -translate-x-1/2')}
          {polePip('minus', 'left', 'left-0 bottom-2 -translate-x-1/2')}
          {polePip('plus', 'top', 'top-0 left-[32%] -translate-x-1/2 -translate-y-1/2')}
          {polePip('minus', 'top', 'top-0 left-[68%] -translate-x-1/2 -translate-y-1/2')}
          {polePip('plus', 'bottom', 'bottom-0 left-[32%] -translate-x-1/2 translate-y-1/2')}
          {polePip('minus', 'bottom', 'bottom-0 left-[68%] -translate-x-1/2 translate-y-1/2')}
        </>
      )}
    </div>
  );
};

export default ElectricalCardNode;
